import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Filter, RefreshCw, Shield, User, FileText, Briefcase, CreditCard, LogIn, Trash2, Download, Eye } from 'lucide-react';

const AuditLogs = () => {
  const navigate = useNavigate();
  const { user, API, language } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    resource_type: '',
    limit: 50,
    skip: 0
  });

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchLogs();
  }, [filters.skip]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      params.append('limit', filters.limit);
      params.append('skip', filters.skip);

      const response = await axios.get(`${API}/admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, skip: 0 }));
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      user_id: '',
      action: '',
      resource_type: '',
      limit: 50,
      skip: 0
    });
    setTimeout(fetchLogs, 100);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login_success':
      case 'login_failed':
        return <LogIn className="w-4 h-4" />;
      case 'account_deletion':
        return <Trash2 className="w-4 h-4" />;
      case 'data_export':
        return <Download className="w-4 h-4" />;
      case 'data_access_request':
        return <FileText className="w-4 h-4" />;
      case 'job_create':
        return <Briefcase className="w-4 h-4" />;
      case 'profile_update':
        return <User className="w-4 h-4" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    if (action.includes('failed') || action.includes('deletion')) return 'bg-red-100 text-red-800';
    if (action.includes('success') || action.includes('create')) return 'bg-green-100 text-green-800';
    if (action.includes('export') || action.includes('request')) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const content = {
    en: {
      title: 'Audit Logs',
      subtitle: 'System activity and compliance tracking',
      searchUser: 'Search by User ID',
      filterAction: 'Filter by Action',
      filterResource: 'Filter by Resource',
      search: 'Search',
      clear: 'Clear',
      refresh: 'Refresh',
      noLogs: 'No audit logs found',
      showing: 'Showing',
      of: 'of',
      logs: 'logs',
      previous: 'Previous',
      next: 'Next',
      allActions: 'All Actions',
      allResources: 'All Resources',
      timestamp: 'Timestamp',
      action: 'Action',
      user: 'User',
      resource: 'Resource',
      details: 'Details',
      ipAddress: 'IP Address'
    },
    de: {
      title: 'Prüfprotokolle',
      subtitle: 'Systemaktivität und Compliance-Tracking',
      searchUser: 'Nach Benutzer-ID suchen',
      filterAction: 'Nach Aktion filtern',
      filterResource: 'Nach Ressource filtern',
      search: 'Suchen',
      clear: 'Löschen',
      refresh: 'Aktualisieren',
      noLogs: 'Keine Prüfprotokolle gefunden',
      showing: 'Zeige',
      of: 'von',
      logs: 'Protokolle',
      previous: 'Zurück',
      next: 'Weiter',
      allActions: 'Alle Aktionen',
      allResources: 'Alle Ressourcen',
      timestamp: 'Zeitstempel',
      action: 'Aktion',
      user: 'Benutzer',
      resource: 'Ressource',
      details: 'Details',
      ipAddress: 'IP-Adresse'
    }
  };

  const t = content[language] || content.en;

  const actionOptions = [
    { value: '', label: t.allActions },
    { value: 'login_success', label: 'Login Success' },
    { value: 'login_failed', label: 'Login Failed' },
    { value: 'account_deletion', label: 'Account Deletion' },
    { value: 'data_export', label: 'Data Export' },
    { value: 'data_access_request', label: 'Data Access Request' },
    { value: 'job_create', label: 'Job Created' },
    { value: 'profile_update', label: 'Profile Update' }
  ];

  const resourceOptions = [
    { value: '', label: t.allResources },
    { value: 'auth', label: 'Authentication' },
    { value: 'user', label: 'User' },
    { value: 'job', label: 'Job' },
    { value: 'document', label: 'Document' },
    { value: 'payment', label: 'Payment' },
    { value: 'gdpr', label: 'GDPR' }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="audit-logs-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : language === 'fr' ? 'Retour' : 'Zurück'}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                placeholder={t.searchUser}
                value={filters.user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
              />
              <Select value={filters.action} onValueChange={(value) => setFilters(prev => ({ ...prev, action: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t.filterAction} />
                </SelectTrigger>
                <SelectContent>
                  {actionOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value || 'all'}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.resource_type} onValueChange={(value) => setFilters(prev => ({ ...prev, resource_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t.filterResource} />
                </SelectTrigger>
                <SelectContent>
                  {resourceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value || 'all'}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button onClick={handleSearch} className="flex-1">
                  <Search className="w-4 h-4 mr-2" />
                  {t.search}
                </Button>
                <Button variant="outline" onClick={handleClearFilters}>
                  {t.clear}
                </Button>
                <Button variant="outline" onClick={fetchLogs}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="mb-4 text-sm text-gray-600">
          {t.showing} {Math.min(filters.skip + 1, total)}-{Math.min(filters.skip + filters.limit, total)} {t.of} {total} {t.logs}
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t.noLogs}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.timestamp}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.action}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.user}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.resource}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.details}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.ipAddress}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log, index) => (
                      <tr key={log.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${getActionColor(log.action)} flex items-center gap-1 w-fit`}>
                            {getActionIcon(log.action)}
                            {log.action}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                          {log.user_id?.substring(0, 8)}...
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-gray-900">{log.resource_type}</span>
                          {log.resource_id && (
                            <span className="text-gray-500 text-xs block">{log.resource_id.substring(0, 8)}...</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {JSON.stringify(log.details)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > filters.limit && (
          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              onClick={() => setFilters(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              disabled={filters.skip === 0}
            >
              {t.previous}
            </Button>
            <span className="text-sm text-gray-600">
              Page {Math.floor(filters.skip / filters.limit) + 1} of {Math.ceil(total / filters.limit)}
            </span>
            <Button
              variant="outline"
              onClick={() => setFilters(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              disabled={filters.skip + filters.limit >= total}
            >
              {t.next}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AuditLogs;
